import { Link } from "react-router-dom";
import { extractErrorMessage } from "@/api/client";
import type { CollectionTemplateBundleDto } from "@/api/collectionBundles";

// WS2 — Step 2 "What they'll work on" of the Send Signup Link wizard.
// Bundles are optional: staff can stand the account up first and assign
// later. The filtering (audience match) is owned by the parent page so
// this component stays presentational.

export interface Step2BundlesProps {
  bundles: CollectionTemplateBundleDto[];
  selectedBundleIds: string[];
  onToggleBundle: (id: string) => void;
  isLoading: boolean;
  error: unknown;
}

export function Step2Bundles({
  bundles,
  selectedBundleIds,
  onToggleBundle,
  isLoading,
  error,
}: Step2BundlesProps) {
  return (
    <div className="flex flex-col gap-space-3">
      <p className="text-small text-gray-mid m-0 max-w-reading">
        <strong className="text-onyx font-display">Bundles</strong> group
        together the documents and forms you'll need from this party.
        Selecting a bundle creates a package they can complete from their
        portal. You can leave this empty for now and assign bundles later
        from the account page.
      </p>

      <section className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow">
          Assignment groups
        </h3>
        <div className="flex flex-col gap-space-2 bg-white-pure rounded-lg border border-border-light p-space-4">
          <p className="text-caption text-gray-mid m-0">
            Optional — leave empty to stand up the account and assign groups
            later.
          </p>
          {isLoading && (
            <p className="text-caption text-gray-mid m-0">Loading bundles…</p>
          )}
          {error ? (
            <p className="text-caption text-status-error m-0" role="alert">
              {extractErrorMessage(error)}
            </p>
          ) : null}
          {!isLoading && bundles.length === 0 && (
            <p className="text-caption text-gray-mid m-0">
              No active bundles match the chosen party type yet.{" "}
              <Link to="/admin/collection-bundles" className="underline">
                Author one
              </Link>
              .
            </p>
          )}
          {bundles.map((b) => {
            const checked = selectedBundleIds.includes(b.id);
            return (
              <label
                key={b.id}
                className="flex items-start gap-space-2 text-small text-onyx"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleBundle(b.id)}
                />
                <span className="flex flex-col">
                  <span className="font-medium">{b.name}</span>
                  <span className="text-caption text-gray-mid">
                    {b.slots.length} item(s)
                    {b.audience ? ` · ${b.audience}` : ""}
                    {b.description ? ` · ${b.description}` : ""}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </section>
    </div>
  );
}
